import { rem } from 'polished';
import React, { useContext } from 'react';
import styled, { css, keyframes, ThemeProvider, ThemeContext } from 'styled-components';

import { translate } from '@vimeo/i18n-runtime';
import { Button as IrisButton } from '@vimeo/iris/components';
import { Text } from '@vimeo/iris/typography';

import { bytesToSize } from '../../utils/bytesToSize';
import { QuotaMood, QuotaPeriod, QuotaUnit } from '../quota';

type StorageTextVariant = 'percent' | 'ratio';

interface Props {
  used?: number;
  max?: number;
  period?: QuotaPeriod;
  unit?: QuotaUnit;
  mood?: QuotaMood;
  textVariant?: StorageTextVariant;
  onClick: () => void;
  className?: string;
}

const getAriaLabel = ({
  period,
  percentUsed,
}: {
  period: QuotaPeriod;
  percentUsed: number;
}): string => {
  switch (period) {
    case 'lifetime':
      return translate({
        singular: '{percentUsed}% of lifetime quota used',
        replacements: { percentUsed },
      });
    case 'year':
      return translate({
        singular: '{percentUsed}% of annual quota used',
        replacements: { percentUsed },
      });
    case 'month':
      return translate({
        singular: '{percentUsed}% of monthly quota used',
        replacements: { percentUsed },
      });
    case 'week':
    default:
      return translate({
        singular: '{percentUsed}% of weekly quota used',
        replacements: { percentUsed },
      });
  }
};

const getButtonText = (
  textVariant: StorageTextVariant,
  unit: QuotaUnit,
  period: QuotaPeriod,
  used: number,
  max: number,
): string => {
  const percentUsed = Math.round((used / max) * 100);
  const replacements = {
    percentUsed: <StyledText key="used-text">{percentUsed}%</StyledText>,
  };

  switch (unit) {
    case 'video_count':
      if (textVariant === 'ratio') {
        switch (period) {
          case 'week':
            return translate({
              singular: 'Weekly videos {STYLE}{used}/{max}{/STYLE}',
              replacements: {
                used: used,
                max: max,
                STYLE: (text: string) => <StyledText key="used-text">{text}</StyledText>,
              },
            });
            break;
          case 'month':
            return translate({
              singular: 'Monthly videos {STYLE}{used}/{max}{/STYLE}',
              replacements: {
                used: used,
                max: max,
                STYLE: (text: string) => <StyledText key="used-text">{text}</StyledText>,
              },
            });
            break;
          case 'year':
            return translate({
              singular: 'Yearly videos {STYLE}{used}/{max}{/STYLE}',
              replacements: {
                used: used,
                max: max,
                STYLE: (text: string) => <StyledText key="used-text">{text}</StyledText>,
              },
            });
            break;
          case 'lifetime':
            return translate({
              singular: 'Total videos {STYLE}{used}/{max}{/STYLE}',
              replacements: {
                used: used,
                max: max,
                STYLE: (text: string) => <StyledText key="used-text">{text}</StyledText>,
              },
            });
            break;
        }
      }

      return translate({
        singular: 'Video usage {percentUsed}',
        replacements,
      });

    case 'video_size':
    default:
      if (textVariant === 'ratio') {
        return translate({
          singular: 'Storage {STYLE}{used}/{max}{/STYLE}',
          replacements: {
            used: bytesToSize(used),
            max: bytesToSize(max),
            STYLE: (text: string) => <StyledText key="used-text">{text}</StyledText>,
          },
        });
      }

      return translate({
        singular: 'Storage {percentUsed}',
        replacements,
      });
  }
};

export function StoragePill({
  used = 0,
  max = 100,
  period = 'week',
  unit = 'video_size',
  mood = 'safe',
  textVariant = 'percent',
  onClick,
  className,
}: Props) {
  const ratioUsed = used / max;
  const percentUsed = Math.round(ratioUsed * 100);

  const theme = useContext(ThemeContext);

  let btnTheme = {
    StoragePill: {
      bg: theme.elements.bg1,
      fg: theme.elements.color4,
      border: theme.elements.color4,
      hoverBg: theme.elements.bg1,
      hoverBorder: theme.elements.color4,
      numberFg: theme.formats.primary,
    },
  };

  switch (mood) {
    case 'safe':
      btnTheme = {
        StoragePill: {
          bg: theme.elements.bg1,
          fg: theme.elements.color4,
          border: theme.elements.color4,
          hoverBg: theme.elements.bg1,
          hoverBorder: theme.elements.color4,
          numberFg: theme.formats.primary,
        },
      };
      break;
    case 'warn':
      btnTheme = {
        StoragePill: {
          bg: theme.name === 'dark' ? 'rgba(55, 37, 0, 1)' : 'rgba(255, 247, 232, 1)',
          fg: theme.elements.color4,
          border: 'rgba(255, 178, 30, 1)',
          hoverBg: theme.name === 'dark' ? 'rgba(55, 37, 0, 1)' : 'rgba(255, 247, 232, 1)',
          hoverBorder: 'rgba(255, 178, 30, 1)',
          numberFg: 'rgba(255, 178, 30, 1)',
        },
      };
      break;
    case 'limit':
      btnTheme = {
        StoragePill: {
          bg: theme.name === 'dark' ? 'rgba(56, 9, 9, 1)' : 'rgba(255, 237, 237, 1)',
          fg: theme.elements.color4,
          border: 'rgba(226, 43, 18, 1)',
          hoverBg: theme.name === 'dark' ? 'rgba(56, 9, 9, 1)' : 'rgba(255, 237, 237, 1)',
          hoverBorder: 'rgba(226, 43, 18, 1)',
          numberFg: 'rgba(226, 43, 18, 1)',
        },
      };
      break;
  }

  const shouldBounce =
    mood === 'warn' ||
    mood === 'limit' ||
    (mood === 'safe' && ratioUsed > 2 / 3 && ratioUsed < 3 / 4);

  return (
    <ThemeProvider theme={btnTheme}>
      <Button
        size="sm"
        bounce={shouldBounce}
        format="secondary"
        variant="outline"
        aria-label={getAriaLabel({ period, percentUsed })}
        pill
        onClick={onClick}
        id="essential-footer-storage-pill"
        className={className}
      >
        <Text style={{ fontWeight: 500 }}>
          {getButtonText(textVariant, unit, period, used, max)}
        </Text>
      </Button>
    </ThemeProvider>
  );
}

const bounceAnimation = keyframes`
  0%, 100% {
    transform: translate(0);
  }
  33% {
    transform: translateY(${rem(5)});
  }
  66% {
    transform: translateY(${rem(-12)});
  }
`;

const animation = css`
  animation: ${bounceAnimation} 600ms ease-out 2s 1;
`;

const Button = styled(IrisButton)`
  margin-right: ${rem(7)};
  background-color: ${({ theme }) => theme.StoragePill.bg};
  color: ${({ theme }) => theme.StoragePill.fg};
  border: ${({ theme }) => `1px solid ${theme.StoragePill.border}`};
  box-shadow: rgba(0, 0, 0, 0.04) 0px 0.1875rem 0.375rem 0px;
  ${({ bounce }: { bounce: boolean }) => (bounce ? animation : null)};
  padding: ${rem(2)} ${rem(14)};

  &:hover {
    background-color: ${({ theme }) => theme.StoragePill.hoverBg};
    border: ${({ theme }) => `1px solid ${theme.StoragePill.hoverBorder}`};
    box-shadow: rgba(0, 0, 0, 0.07) 0px 0.1875rem 0.375rem 0px;
    transform: translateY(-1px) scale(1.001);
  }
`;

const StyledText = styled(Text)`
  color: ${({ theme }) => theme.StoragePill.numberFg};
  font-weight: 800;
`;
